export const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * runs `fn` `totalAttempts` times
 * @param onError can throw to cancel retries
 */
export const retry = async <T>(
  fn: () => Promise<T>,
  totalAttempts = 2,
  onError?: (e: unknown, attempt: number) => boolean | void,
): Promise<T> => {
  let lastError: unknown | undefined;
  for (let r = 0; r < totalAttempts; r++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      onError?.(e, r + 1);
    }
  }

  throw lastError;
};

export const runBulk = async <T>(
  items: readonly T[],
  fn: (item: T) => Promise<void>,
  { concurrency = 5 }: { concurrency?: number } = {},
): Promise<void> => {
  if (items.length === 0) return;

  let index = 0;

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    for (;;) {
      const i = index++;
      if (i >= items.length) return;
      await fn(items[i]!); // is safe here -> otherwise ts error
    }
  });

  await Promise.all(workers);
};

export const hasOwnProperty = <K extends PropertyKey>(value: unknown, property: K): value is Record<K, unknown> =>
  value !== null && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, property);
